import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { PromiseUtilsService } from 'src/app/shared/services/promise-utils.service';
import { AIChatbotService } from '../../services/ai-chatbot/ai-chatbot.service';
import { AiChatbotTemplateTunerGeneration } from '../../models/ai-chatbot-presentation-data';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ai-chatbot-loading',
  templateUrl: './ai-chatbot-loading.component.html',
  styleUrl: './ai-chatbot-loading.component.scss',
  standalone: false,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate(
          '3s ease-in',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
  ],
})
export class AiChatbotLoadingComponent implements OnInit, OnDestroy {
  @ViewChild('samplesContainer', { static: false })
  samplesContainer!: ElementRef;

  shownSamples: number = 0;
  showGeneratingSamplesSubscription: Subscription;
  private sseLoadingSubscription: Subscription;
  private ssePartialSubscription: Subscription;

  loadingText: string = 'Thinking...';
  sseIsEditing: boolean = false;
  partialTemplates: AiChatbotTemplateTunerGeneration[] = [];
  private generationAbort = { canceled: false };

  get isPortrait(): boolean {
    return this.partialTemplates.some((t) => t?.orientation === 'portrait');
  }

  constructor(
    public aiChatbotService: AIChatbotService,
    private promiseUtilsService: PromiseUtilsService,
  ) {}

  ngOnInit(): void {
    this.subscribeToShowGeneratingSamples();
    this.subscribeToSseLoadingMessages();
    this.subscribeToSsePartialTemplates();

    if (this.aiChatbotService.isLoadingTemplates && this.shownSamples === 0) {
      this.showGeneratingSamples();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.showGeneratingSamplesSubscription) {
      this.showGeneratingSamplesSubscription.unsubscribe();
      this.showGeneratingSamplesSubscription = null;
    }
    if (this.sseLoadingSubscription) {
      this.sseLoadingSubscription.unsubscribe();
      this.sseLoadingSubscription = null;
    }
    if (this.ssePartialSubscription) {
      this.ssePartialSubscription.unsubscribe();
      this.ssePartialSubscription = null;
    }
  }

  private subscribeToShowGeneratingSamples(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.showGeneratingSamplesSubscription) {
      this.showGeneratingSamplesSubscription.unsubscribe();
    }

    this.showGeneratingSamplesSubscription =
      this.aiChatbotService.showLoadingSamples$.subscribe(() => {
        this.showGeneratingSamples();
      });
  }

  private subscribeToSseLoadingMessages(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.sseLoadingSubscription) {
      this.sseLoadingSubscription.unsubscribe();
    }

    this.sseLoadingSubscription =
      this.aiChatbotService.sseLoadingMessage$.subscribe((event) => {
        // SSE messages override the simulated loading progress
        this.generationAbort.canceled = true;
        this.loadingText = event.message;
        this.sseIsEditing = event.data === 'editing';
        if (this.sseIsEditing) {
          this.shownSamples = 1;
        } else {
          this.partialTemplates = [];
          this.shownSamples = 3;
        }
      });
  }

  private subscribeToSsePartialTemplates(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.ssePartialSubscription) {
      this.ssePartialSubscription.unsubscribe();
    }

    this.ssePartialSubscription =
      this.aiChatbotService.ssePartialTemplates$.subscribe(
        ({ partial, generationIndex }) => {
          // Place each partial at its designated index to preserve ordering
          const updated = [...this.partialTemplates];
          updated[generationIndex] = partial;
          this.partialTemplates = updated;
          this.shownSamples = Math.max(this.shownSamples, generationIndex + 1);
          this.scrollPresentationsSamplesToRight();
        },
      );
  }

  async showGeneratingSamples(): Promise<void> {
    this.generationAbort = { canceled: false };
    const abort = this.generationAbort;

    this.sseIsEditing = false;
    this.shownSamples = 0;
    this.partialTemplates = [];

    this.loadingText = 'Thinking...';

    await this.promiseUtilsService.delay(6000);
    if (abort.canceled) return;
    this.loadingText = 'Generating presentations...';

    await this.promiseUtilsService.delay(4000);
    if (abort.canceled) return;
    this.shownSamples = 1;
    await this.scrollPresentationsSamplesToRight();

    await this.promiseUtilsService.delay(2000);
    if (abort.canceled) return;
    this.shownSamples = 2;
    await this.scrollPresentationsSamplesToRight();

    await this.promiseUtilsService.delay(2000);
    if (abort.canceled) return;
    this.shownSamples = 3;
    await this.scrollPresentationsSamplesToRight();

    await this.promiseUtilsService.delay(2000);
    if (abort.canceled) return;
    this.loadingText = 'Almost there...';
  }

  scrollPresentationsSamplesToRight = async () => {
    if (this.samplesContainer) {
      await this.promiseUtilsService.delay(100);
      const element = this.samplesContainer.nativeElement;
      element.scrollTo({
        left: element.scrollWidth,
        behavior: 'smooth',
      });
    }
  };
}
